import { Injectable, OnInit, OnDestroy, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Subscription } from 'rxjs';

import { OidcSecurityService} from 'angular-auth-oidc-client';
import { SettingsService } from './settings.service';
import { StorageManagerService } from './storage-manager.service'
import { Utilities } from './utilities';
import { IUserLoginData, IUserLoginApplication } from '../models/index';
// import { datadogRum } from '@datadog/browser-rum';

@Injectable()
export class AuthenticationService implements OnInit, OnDestroy {

  isAuthorized: boolean;
  logoutStarted = false;
  subscription: Subscription;
  ddBrowserRum;

	constructor (private oidcSecurityService: OidcSecurityService, private http: HttpClient,
		private settingsService: SettingsService, private storageManagerService: StorageManagerService, private ngzone: NgZone) {

	}

	ngOnInit() {
    this.subscription = this.oidcSecurityService.isAuthenticated$.subscribe(
			(isAuthorized: boolean) => {
				this.isAuthorized = isAuthorized;
      });
  }

	ngOnDestroy(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
    }
	}

	getIsAuthorized(): Observable<boolean> {
    return this.oidcSecurityService.isAuthenticated$;
	}

	getUserData(): Observable<any> {
    return this.oidcSecurityService.userData$;
	}

	login() {
		console.log('start login');
		this.oidcSecurityService.authorize();
	}

	refreshSession() {
		console.log('start refreshSession');
    this.oidcSecurityService.forceRefreshSession().subscribe((result) => console.log(result));
	}

  logout() {
    console.log('start logoff');
    this.logoutStarted = true;
    this.oidcSecurityService.logoff();
  }

  // Logout
  public captureLogoutEvent(displayableId: string) {
    return this.get(`${ window.location.origin }/api/user/logout?loginName=${ displayableId }&appName=SingleSource`);
  }

	getBearerToken(): string {
		const token = this.oidcSecurityService.getToken();
		if (token === '') { return ''; }

    return 'Bearer ' + token;
  }

  get(url: string): Observable<any> {

    return this.http.get<any>(url, { headers: this.getHeaders() });
  }

  post(url: string, data: any): Observable<any> {

    const body = JSON.stringify(data);
    return this.http.post<any>(url, body, { headers: this.getHeaders() });
  }

  private getHeaders(setContentType?: boolean, isApplicationLevel?: boolean) {

    let authData: IUserLoginData = this.getAuthData();

    if (!authData) {
      authData = authData ? authData : this.storageManagerService.read('userData');

      if (!authData) {
        this.getUserData().subscribe(res => {
          if (res) {
            this.storageManagerService.write('userData', res);
            authData = this.getAuthData();
          }
        });
      }
    } else {

      let applicationVariantId = authData.applicationVariantId ? authData.applicationVariantId.toString() : '';
      let customer = this.storageManagerService.read('currentCustomer');
      const applicationName = authData.applicationName;
      const applicationVariantName = authData.applicationVariantName;

      if (customer && !Utilities.TestIsObjectEmpty(customer)) {

        if (typeof customer !== 'object') {
          customer = JSON.parse(customer);
        }
        authData.customerGuid = customer.customerGuid.toString();
        if (customer.applicationVariants.length === 1) {
          applicationVariantId = customer.applicationVariants[0].applicationVariantId.toString();
        }
      } else {
        authData.customerGuid = '';
      }

      let headers;
      if (isApplicationLevel) {
        headers = new HttpHeaders(
          {
            'customerGuid': authData.customerGuid ? authData.customerGuid : '',
            'applicationName': applicationName
          });
      } else {
        headers = new HttpHeaders(
          {
            'customerGuid': authData.customerGuid ? authData.customerGuid : '',
            'applicationVariantId': applicationVariantId,
            'applicationName': applicationName,
            'applicationVariantName': applicationVariantName
          });
      }

      if (setContentType) {
        headers = headers.set('content-type', 'application/json');
      }

      return this.appendAuthHeader(headers);
    }
  }

  private appendAuthHeader(headers: HttpHeaders) {

    const token = this.oidcSecurityService.getToken();

    if (token === '') { return headers; }

    const tokenValue = 'Bearer ' + token;
    headers = headers.set('authorization', tokenValue);

    return headers;
  }

  public getAuthData(): any {
    let applications: IUserLoginApplication[];
    let appVariantId: number;
    let authData: IUserLoginData;
    let appVariantName: string;
    let applicationName: string;

    authData = this.storageManagerService.read('userData');

    if (authData) {

      if (authData.applications && typeof authData.applications !== 'object') {
        applications = JSON.parse(authData.applications as any);
      }

      if (applications && applications.length > 0) {

        const dataScope = this.storageManagerService.read('dataScope');

        if (dataScope) {

          appVariantId = +applications.find(a => a.AVName.toLowerCase() === dataScope.toLowerCase()).AVId;
          appVariantName = applications.find(a => a.AVName.toLowerCase() === dataScope.toLowerCase()).AVName.toLowerCase();
          applicationName = applications[0].AName;
        } else {
          // temporarily until the spike is resolved
          appVariantId = 251;
          applicationName = 'SingleSource';
          appVariantName = 'eudamed';
        }

        if (authData.userguid && appVariantId) {

          authData.userGuid = authData.userguid;
          authData.applicationVariantId = appVariantId.toString();
          authData.applicationVariantName = appVariantName;
          authData.applicationName = applicationName;
        }
      }
      return authData;
    }
  }

  public setHeader(setContentType?: boolean, isApplicationLevel?: boolean): HttpHeaders {

    return this.getHeaders(setContentType, isApplicationLevel);
  }

  // Initialize datadog script element
  async initDataDogScriptElement() {
    // set datadog script element
    this.ddBrowserRum = await import(/* webpackChunkName: "datadog-browser-rum" */'@datadog/browser-rum');
  }

  initPendo(userguid, customerName) {
    this.ngzone.runOutsideAngular(function () {
      (function (apiKey) {
        (function (p, e, n, d, o) {

          let v, w, x, y, z; o = p[d] = p[d] || {}; o._q = [];

          v = ['initialize', 'identify', 'updateOptions', 'pageLoad'];

          for (w = 0, x = v.length; w < x; ++w) {

            (function (m) {
              o[m] = o[m] || function () { o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0))); };
            })(v[w]);
          }

          y = e.createElement(n); y.async = !0; y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
          z = e.getElementsByTagName(n)[0]; z.parentNode.insertBefore(y, z);
        })(window, document, 'script', 'pendo');

        // Please use Strings, Numbers, or Bools for value types.
        window['pendo'].initialize({
          visitor: {
            id: userguid  // Required if user is logged in
          },

          account: {
            id: customerName

            // You can add any additional account level key-values here,
            // as long as it's not one of the above reserved names.
          }
        });
      })('d61d92d8-e1ef-4a59-4f1f-3f8c9f94cbd8');
    });
  }

  // Set script element attribute
  setScriptAttribute(srcJs, attClass, attName) {
    this.ngzone.runOutsideAngular(function () {
      (function (srcJs, attClass, attName) {
        (function (d) {

          var sourceScripts = d.getElementsByTagName('script');

          for (var i = 0; i < sourceScripts.length; i++) {
            if (sourceScripts[i].src.indexOf(srcJs) != -1) {
              sourceScripts[i].removeAttribute(attName);
              sourceScripts[i].setAttribute(attName, attClass);
            }
          }
        })(document);
      })(srcJs, attClass, attName);
    });
  }

 // Initialize DataDog monitering service
  initDataDogRUM() {
    // Init DataDog monitoring service if the flag is on
    const bIsDataDogEnabled = this.settingsService.dataDogEnabled && this.settingsService.dataDogEnabled.toString().toLowerCase() === 'true' ? true : false;
    if (bIsDataDogEnabled) {
      this.ddBrowserRum.datadogRum?.init({
        applicationId: this.settingsService.dataDogApplicationId,
        clientToken: this.settingsService.dataDogClientToken,
        site: this.settingsService.dataDogSite,
        service: this.settingsService.DataDogService,
        env: this.settingsService.dataDogEnv,
        version: this.settingsService.dataDogVersion,
        sampleRate: this.settingsService.dataDogSampleRate ? +this.settingsService.dataDogSampleRate : 100,  // set default to 100%
        silentMultipleInit: true,
        trackInteractions: this.settingsService.dataDogTrackInteractions && this.settingsService.dataDogTrackInteractions.toString().toLowerCase() === 'true' ? true : false,
        allowedTracingOrigins: this.settingsService.dataDogAllowedTracingOrigins ? this.settingsService.dataDogAllowedTracingOrigins.replace(/ /g, '').split(',') : []
      });
    }
  }
}
